import { Injectable, signal } from '@angular/core';
import { Toast, ToastBuilder } from '../models/Toast';
import { Observable, tap } from 'rxjs';

/** A function lets the caller build the message from the actual value - a server-sent reason, a count from the response. */
export type ToastMessage<T> = string | ((value: T) => string);
export type ToastErrorMessage = ToastMessage<unknown>;

function resolveMessage<T>(message: ToastMessage<T>, value: T): string {
  return typeof message === 'function' ? message(value) : message;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly _toasts = signal<Toast[]>([]);

  get toasts() {
    return this._toasts();
  }

  hasObservableToasts() {
    return this._toasts().some((toast) => toast.type === 'observable');
  }

  createAndAddToast(toastBuilder: ToastBuilder) {
    const type = toastBuilder.type;

    switch (type) {
      case 'success':
        this.addSuccessToast(toastBuilder.message);
        break;
      case 'error':
        this.addErrorToast(toastBuilder.message);
        break;
      case 'warning':
        this.addWarningToast(toastBuilder.message);
        break;
      default:
        break;
    }
  }

  addSuccessToast(message: string) {
    const successToast = this.createSuccessToast(message);

    this.updateToasts(successToast);
  }

  addErrorToast(message: string) {
    const errorToast = this.createErrorToast(message);

    this.updateToasts(errorToast);
  }

  addWarningToast(message: string) {
    const warningToast = this.createWarningToast(message);

    this.updateToasts(warningToast);
  }

  /** `successMessage: null` keeps the loading toast but shows no success toast afterwards. */
  runWithToast<T>(
    obs$: Observable<T>,
    loadingMessage: string | null,
    successMessage: ToastMessage<T> | null,
    errorMessage: ToastErrorMessage,
  ) {
    return this.tapToast<T>(loadingMessage, successMessage, errorMessage)(obs$) as Observable<T>;
  }

  private tapToast<T>(
    loadingMessage: string | null,
    successMessage: ToastMessage<T> | null,
    errorMessage: ToastErrorMessage,
  ) {
    const observableToast = loadingMessage ? this.createObservableToast(loadingMessage) : null;
    this._toasts.update((toasts) => (observableToast ? [observableToast, ...toasts] : [...toasts]));

    return (source$: Observable<T>) =>
      source$.pipe(
        tap({
          next: (value: T) => {
            if (observableToast) {
              this.removeToast(observableToast.id);
            }
            if (successMessage === null) return;

            const successToast = this.createSuccessToast(resolveMessage(successMessage, value));

            this.updateToasts(successToast);
          },
          error: (error: unknown) => {
            if (observableToast) {
              this.removeToast(observableToast.id);
            }
            const errorToast = this.createErrorToast(resolveMessage(errorMessage, error));

            this.updateToasts(errorToast);
          },
        }),
      );
  }

  toastOnError<T>(obs$: Observable<T>, errorMessage: string): Observable<T> {
    return ((source$: Observable<T>) =>
      source$.pipe(
        tap({
          error: (_: unknown) => {
            const errorToast = this.createErrorToast(errorMessage);
            this.updateToasts(errorToast);
          },
        }),
      ))(obs$) as Observable<T>;
  }

  startToastRemovalTimer(id: string, timeout: number) {
    setTimeout(() => {
      this.removeToast(id);
    }, timeout);
  }

  updateToasts(toast: Toast) {
    this._toasts.update((toasts) => [toast, ...toasts]);
    this.startToastRemovalTimer(toast.id, toast.lifetime);
  }

  private createSuccessToast(message: string, lifetime = 3000): Toast {
    return {
      id: crypto.randomUUID(),
      message: message,
      type: 'success',
      lifetime,
    };
  }

  private createErrorToast(message: string, lifetime = 3000): Toast {
    return {
      id: crypto.randomUUID(),
      message: message,
      type: 'error',
      lifetime,
    };
  }

  private createWarningToast(message: string, lifetime = 3000): Toast {
    return {
      id: crypto.randomUUID(),
      message: message,
      type: 'warning',
      lifetime,
    };
  }

  private createObservableToast(message: string, lifetime = 3000): Toast {
    return {
      id: crypto.randomUUID(),
      message: message,
      type: 'observable',
      lifetime,
    };
  }

  private removeToast(id: string) {
    const toastIndex = this._toasts().findIndex((toast) => toast.id === id);
    if (toastIndex === -1) return;

    this._toasts().splice(toastIndex, 1);
    this._toasts.update((toasts) => [...toasts]);
  }
}
